import { MILLISECONDS_WE_CONSIDER_GOAL_NEW } from '@consts';
import PercentDiscountIcon from '@icons/PercentDiscountIcon';
import RocketIcon from '@icons/RocketIcon';
import SoldOutIcon from '@icons/SoldOutIcon';
import { setupForDark } from '@theme';
import classNames from 'classnames';
import { GoalSellingStatus } from 'lib/models/goals';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface GoalSellingStatusBannerProps extends React.HTMLAttributes<HTMLDivElement> {
  sellingStatus: GoalSellingStatus;
  entityCreatedAt: Date | null;
}

type BannerKind = {
  Icon: React.ComponentType<{ size?: number; className?: string }>;
  textKey: string;
  gradient: string;
};

const GREEN_GRADIENT = 'bg-gradient-to-r from-[#358C63] to-[#6EC29A]';
const RED_GRADIENT = 'bg-gradient-to-r from-[#CB4141] to-[#F17B7B]';

function Banner({ Icon, textKey, gradient }: BannerKind) {
  const { t } = useTranslation();
  return (
    <div className='absolute left-[-69px] top-[-70px] flex items-center justify-center size-[273px] pointer-events-none z-10'>
      <div className='-rotate-45 flex-none'>
        <div
          className={classNames(
            'flex items-center justify-center gap-xs p-xs w-[350px]',
            gradient,
            'shadow-[0px_4px_4px_rgba(0,0,0,0.5)]'
          )}
        >
          <Icon size={20} className={setupForDark('text-white')} />
          <span className={classNames('P5B sm:P4B uppercase', setupForDark('text-white'))}>
            {t(textKey)}
          </span>
        </div>
      </div>
    </div>
  );
}

export default function GoalSellingStatusBanner({
  sellingStatus,
  entityCreatedAt
}: GoalSellingStatusBannerProps) {
  const createdAtMs = entityCreatedAt ? new Date(entityCreatedAt).getTime() : null;
  const now = Date.now();
  const isNewGoal =
    typeof createdAtMs === 'number' &&
    Number.isFinite(createdAtMs) &&
    createdAtMs <= now &&
    createdAtMs >= now - MILLISECONDS_WE_CONSIDER_GOAL_NEW;

  if (sellingStatus === GoalSellingStatus.ON_SALE && isNewGoal) {
    return (
      <Banner
        Icon={RocketIcon}
        textKey='domains.goals.sellingStatus.new'
        gradient={GREEN_GRADIENT}
      />
    );
  }
  if (sellingStatus === GoalSellingStatus.PRE_SALE) {
    return (
      <Banner
        Icon={PercentDiscountIcon}
        textKey='domains.goals.sellingStatus.preSale'
        gradient={GREEN_GRADIENT}
      />
    );
  }
  if (sellingStatus === GoalSellingStatus.SOLD_OUT) {
    return (
      <Banner
        Icon={SoldOutIcon}
        textKey='domains.goals.sellingStatus.soldOut'
        gradient={RED_GRADIENT}
      />
    );
  }
  return null;
}
